import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Upload } from 'lucide-react';
import { ProjectFileCard } from './ProjectFileCard';
function LoadingSkeleton() {
    return /*#__PURE__*/ _jsx("div", {
        className: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4",
        children: [
            1,
            2,
            3,
            4,
            5,
            6
        ].map((i)=>/*#__PURE__*/ _jsx("div", {
                className: "bg-gray-100 rounded-lg h-32 animate-pulse",
                "data-testid": `skeleton-${i}`
            }, i))
    });
}
function EmptyState({ canUpload, searchQuery }) {
    // Show search-specific message when there's a search query
    if (searchQuery) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col items-center justify-center py-16 px-4",
            "data-testid": "empty-search-state",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center",
                    children: /*#__PURE__*/ _jsx(Upload, {
                        className: "w-8 h-8 text-gray-400"
                    })
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-gray-600 font-medium mb-1",
                    children: "No files match your search"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-gray-500 text-center",
                    children: "Try adjusting your search terms"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col items-center justify-center py-16 px-4",
        "data-testid": "empty-state",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center",
                children: /*#__PURE__*/ _jsx(Upload, {
                    className: "w-8 h-8 text-gray-400"
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-gray-600 font-medium mb-1",
                children: "No files yet"
            }),
            canUpload && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-gray-500 text-center",
                children: "Drag and drop files here or click Upload to get started"
            })
        ]
    });
}
function FileGridContent({ files, isLoading, canDelete, canUpload, onDelete, onStartChat, onFileClick, isCreatingChat, deletingFileIds, searchQuery }) {
    if (isLoading) {
        return /*#__PURE__*/ _jsx(LoadingSkeleton, {});
    }
    if (files.length === 0) {
        return /*#__PURE__*/ _jsx(EmptyState, {
            canUpload: canUpload,
            searchQuery: searchQuery
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4",
        "data-testid": "file-list",
        children: files.map((file)=>/*#__PURE__*/ _jsx(ProjectFileCard, {
                file: file,
                onDelete: onDelete,
                onStartChat: onStartChat,
                onClick: onFileClick,
                isCreatingChat: isCreatingChat,
                isDeleting: deletingFileIds?.has(file.attachmentId) ?? false,
                canDelete: canDelete
            }, file.attachmentId))
    });
}
export function ProjectFileGrid({ files, isLoading = false, canDelete = true, canUpload = true, onDelete, onStartChat, onFileClick, isCreatingChat, deletingFileIds, searchQuery, isDragOver, onDragOver, onDragLeave, onDrop }) {
    const content = /*#__PURE__*/ _jsx(FileGridContent, {
        files: files,
        isLoading: isLoading,
        canDelete: canDelete,
        canUpload: canUpload,
        onDelete: onDelete,
        onStartChat: onStartChat,
        onFileClick: onFileClick,
        isCreatingChat: isCreatingChat,
        deletingFileIds: deletingFileIds,
        searchQuery: searchQuery
    });
    // Only render the drop zone wrapper when user can upload
    if (canUpload) {
        return /*#__PURE__*/ _jsxs("div", {
            className: `
          relative min-h-[300px] rounded-lg border-2 border-dashed transition-colors
          ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-transparent'}
        `,
            onDragOver: onDragOver,
            onDragLeave: onDragLeave,
            onDrop: onDrop,
            "data-testid": "file-drop-zone",
            children: [
                isDragOver && /*#__PURE__*/ _jsx("div", {
                    className: "absolute inset-0 flex items-center justify-center bg-blue-50/90 rounded-lg z-10 pointer-events-none",
                    "data-testid": "drag-overlay",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center",
                        children: [
                            /*#__PURE__*/ _jsx(Upload, {
                                className: "w-12 h-12 mx-auto mb-2 text-blue-500"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-blue-600 font-medium text-lg",
                                children: "Drop files to upload"
                            })
                        ]
                    })
                }),
                content
            ]
        });
    }
    // When user can't upload, just render the content without drop zone
    return /*#__PURE__*/ _jsx("div", {
        className: "min-h-[300px]",
        children: content
    });
}
